import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from "@/components/ui";
import { Loader2, FileText, CheckCircle2 } from "lucide-react";

interface TermPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termHtml: string;
  termData?: {
    hedgeCode: string;
    hedgeAmountUsdc: string;
    durationDays: number;
    startDate: string;
    endDate: string;
    lockedAmountBrl: string;
    protectionFeeBrl: string;
    companyName: string;
    companyCnpj: string;
  };
  onAccept: () => void;
  loading?: boolean;
}

export function TermPreviewModal({
  open,
  onOpenChange,
  termHtml,
  termData,
  onAccept,
  loading,
}: TermPreviewModalProps) {
  const [accepted, setAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset ao fechar
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAccepted(false);
      setHasScrolledToBottom(false);
    }
    onOpenChange(newOpen);
  };

  // Detectar scroll até o final
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    // Considera que chegou ao final se estiver a 50px do fim
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col bg-white border-neutral-200 [&>button]:text-neutral-500 [&>button]:ring-offset-white [&>button]:hover:text-neutral-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-neutral-900">
            <FileText className="h-5 w-5 text-emerald-600" />
            Termo de Proteção Cambial Privado
          </DialogTitle>
        </DialogHeader>

        {/* Resumo da operação */}
        {termData && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-neutral-500">Código</span>
                <p className="font-medium text-neutral-900">{termData.hedgeCode}</p>
              </div>
              <div>
                <span className="text-neutral-500">Valor Protegido</span>
                <p className="font-medium text-neutral-900">{termData.hedgeAmountUsdc} USDC</p>
              </div>
              <div>
                <span className="text-neutral-500">Prazo</span>
                <p className="font-medium text-neutral-900">{termData.durationDays} dias</p>
              </div>
              <div>
                <span className="text-neutral-500">Valor Garantido</span>
                <p className="font-medium text-emerald-600">{termData.lockedAmountBrl}</p>
              </div>
            </div>
          </div>
        )}

        {/* Área de scroll com o termo */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto min-h-[300px] max-h-[50vh] border border-neutral-200 rounded-lg bg-white"
        >
          <div
            className="p-6 text-neutral-900 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: termHtml }}
          />
        </div>

        {/* Indicador de scroll */}
        {!hasScrolledToBottom && (
          <p className="text-center text-xs text-neutral-400 py-1">
            Role até o final para aceitar o termo
          </p>
        )}

        {/* Checkbox de aceite */}
        <div className="flex items-start gap-3 py-3 border-t border-neutral-200">
          <input
            type="checkbox"
            id="accept-term"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={!hasScrolledToBottom || loading}
            className="mt-1 h-5 w-5 rounded border-neutral-300 bg-white text-emerald-600 focus:ring-emerald-500 focus:ring-offset-white disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <label
            htmlFor="accept-term"
            className={`text-sm ${hasScrolledToBottom ? "text-neutral-700" : "text-neutral-400"} ${loading ? "opacity-50" : ""}`}
          >
            Li e aceito integralmente os termos do contrato acima. Declaro estar ciente de que esta
            operação é uma proteção cambial e que, ao travar a taxa, abro mão de eventuais ganhos
            caso o câmbio se mova favoravelmente.
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={onAccept}
            disabled={!accepted || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Aceitar e Confirmar Hedge
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
